import { useEffect, useState } from "react";
import "./App.css";
import {
  Button,
  CircularProgress,
  Grid2 as Grid,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import logo from "./logo.png";
import { formatNumber, numberRegex, unformatNumber } from "./utils";
import { isEmpty, validate } from "validate.js";
import axios from "axios";
import moment from "moment";

const App = () => {
  const validateSchema = {
    loanAmount: {
      presence: {
        allowEmpty: false,
      },
    },
    loanType: {
      presence: {
        allowEmpty: false,
      },
    },
    loanDate: {
      presence: {
        allowEmpty: false,
      },
    },
    repaymentStartDate: {
      presence: {
        allowEmpty: false,
      },
    },
    repaymentFrequency: {
      presence: {
        allowEmpty: false,
      },
    },
    interest: {
      presence: {
        allowEmpty: false,
      },
    },
    duration: {
      presence: {
        allowEmpty: false,
      },
    },
  };
  const [formValues, setFormValues] = useState({
    durationOption: "Months",
    interestOption: "Annually",
    repaymentFrequency: "Monthly",
  });
  const [errors, setErrors] = useState([]);
  const [state, setState] = useState("idle");
  const [data, setData] = useState(null);
  const [render, setRender] = useState(true);

  const loanTypes = [
    "Personal Loan",
    "Business Loan",
    "Mortgage Loan",
    "Car Loan",
    "Other",
  ];

  const durations = ["Months", "Years"];

  const repaymentFrequencies = [
    "Semi-Annually",
    "Quarterly",
    "Monthly",
    "Weekly",
    "Daily",
  ];

  const interestOptions = ["Annually", "Monthly"];

  //use effects
  useEffect(() => {}, []);

  //methods
  const handleChange = (e) => {
    const inputName = e?.target?.name;
    const inputValue = e?.target?.value;

    if (
      inputName === "loanAmount" ||
      inputName === "duration" ||
      inputName === "otherCharges" ||
      inputName === "interest"
    ) {
      if (numberRegex.test(inputValue)) {
        setFormValues({
          ...formValues,
          [inputName]: inputValue,
        });
      }
    } else if (inputName === "loanDate") {
      setFormValues({
        ...formValues,
        [inputName]: inputValue,
        repaymentStartDate: undefined,
      });
      setRender(false);
      setTimeout(() => {
        setRender(true);
      }, 400);
    } else {
      setFormValues({
        ...formValues,
        [inputName]: inputValue,
      });
    }
  };

  const getDuration = () => {
    let _duration = 0;
    switch (formValues?.repaymentFrequency) {
      case "Monthly":
        if (formValues?.durationOption === "Months") {
          _duration = unformatNumber(formValues?.duration);
        } else if (formValues?.durationOption === "Years") {
          _duration = unformatNumber(formValues?.duration) * 12;
        }
        break;
      case "Daily":
        if (formValues?.durationOption === "Months") {
          _duration = unformatNumber(formValues?.duration) * 30;
        } else if (formValues?.durationOption === "Years") {
          _duration = unformatNumber(formValues?.duration) * 365;
        }
        break;
      case "Weekly":
        if (formValues?.durationOption === "Months") {
          _duration = parseInt(unformatNumber(formValues?.duration) * 4.35);
        } else if (formValues?.durationOption === "Years") {
          _duration = unformatNumber(formValues?.duration) * 52;
        }
        break;
      case "Semi-Annually":
        if (formValues?.durationOption === "Months") {
          _duration = unformatNumber(formValues?.duration) / 6;
        } else if (formValues?.durationOption === "Years") {
          _duration = unformatNumber(formValues?.duration) * 2;
        }
        break;
      case "Quarterly":
        if (formValues?.durationOption === "Months") {
          _duration = unformatNumber(formValues?.duration) / 3;
        } else if (formValues?.durationOption === "Years") {
          _duration = unformatNumber(formValues?.duration) * 4;
        }
        break;
      default:
        break;
    }

    return _duration;
  };

  const getRate = () => {
    let _interest = 0;
    switch (formValues?.repaymentFrequency) {
      case "Monthly":
        if (formValues?.interestOption === "Annually") {
          _interest = unformatNumber(formValues?.interest) / 12;
        } else if (formValues?.interestOption === "Monthly") {
          _interest = unformatNumber(formValues?.interest);
        }
        break;
      case "Daily":
        if (formValues?.interestOption === "Annually") {
          _interest = unformatNumber(formValues?.interest) / 365;
        } else if (formValues?.interestOption === "Monthly") {
          _interest = unformatNumber(formValues?.interest) / 30;
        }
        break;
      case "Weekly":
        if (formValues?.interestOption === "Annually") {
          _interest = unformatNumber(formValues?.interest) / 52;
        } else if (formValues?.interestOption === "Monthly") {
          _interest = unformatNumber(formValues?.interest) / 4;
        }
        break;
      case "Semi-Annually":
        if (formValues?.interestOption === "Annually") {
          _interest = unformatNumber(formValues?.interest) / 2;
        } else if (formValues?.interestOption === "Monthly") {
          _interest = unformatNumber(formValues?.interest) / 4;
        }
        break;
      case "Quarterly":
        if (formValues?.interestOption === "Annually") {
          _interest = unformatNumber(formValues?.interest) / 52;
        } else if (formValues?.interestOption === "Monthly") {
          _interest = unformatNumber(formValues?.interest) / 4;
        }
        break;
      default:
        break;
    }
    return _interest / 100;
  };

  const handleSubmit = async () => {
    let _errors = validate(formValues, validateSchema);
    if (_errors) {
      setErrors(_errors);
      return;
    } else {
      //check for semi-annually and quarterly, duration should be at least 1 year
      if (
        formValues?.repaymentFrequency === "Semi-Annually" ||
        formValues?.repaymentFrequency === "Quarterly"
      ) {
        if (
          formValues?.durationOption === "Months" &&
          formValues?.duration < 12
        ) {
          _errors = {
            duration: [
              `Duration for ${formValues?.repaymentFrequency} repayment should be at least 12 months`,
            ],
          };
          setErrors(_errors);
          return;
        }
      }
      setErrors([]);
    }

    // setState("loading");

    let payload = {
      loanType: formValues?.loanType,
      amount: unformatNumber(formValues?.loanAmount),
      startDate: moment(formValues?.repaymentStartDate)
        .format("DD-MMM-YYYY")
        ?.toUpperCase(),
      loanDate: moment(formValues?.loanDate)
        .format("DD-MMM-YYYY")
        ?.toUpperCase(),
      period: formValues?.repaymentFrequency?.toLowerCase(),
      duration: getDuration(),
      rate: getRate(),
      otherCharges: unformatNumber(formValues?.otherCharges || 0),
    };

    // alert(JSON.stringify(payload));

    const apiRes = await axios
      .get(`${process.env.REACT_APP_SERVER_URL}/loans/repayment-schedule`, {
        params: payload,
      })
      .then((r) => r)
      .catch((e) => {
        alert(e?.message);
        setState("error");
        setData(e?.message);
      });

    if (apiRes?.status === 200) {
      setState("idle");
      setData(apiRes?.data?.data);
    } else {
      setState("error");
      setData(apiRes?.data);
    }
  };

  const calculateTotalPayment = (addOtherCharges = true) => {
    let total = 0;
    data?.schedule?.forEach((row) => {
      total += row?.payments;
    });
    return formatNumber(
      addOtherCharges ? total + data?.otherCharges : total,
      2
    );
  };

  const calculateTotalInterest = () => {
    let total = 0;
    data?.schedule?.forEach((row) => {
      total += row?.payments;
    });
    return formatNumber(total - data?.loanAmount, 2);
  };

  const calculateTotalPrincipal = () => {
    let total = 0;
    data?.schedule?.forEach((row) => {
      total += row?.principal;
    });
    return formatNumber(total, 2);
  };

  //builders
  const BuildForm = () => {
    return (
      <Grid container spacing={1.5}>
        <Grid item size={{ xs: 12, md: 12 }}>
          <h5>General Loan Details</h5>
        </Grid>
        <Grid item size={{ md: 6, sm: 12, xs: 12 }}>
          <TextField
            required
            fullWidth
            select
            variant="outlined"
            name="loanType"
            id="loan-type"
            label="Loan Type"
            error={errors["loanType"] ? true : false}
            helperText={errors["loanType"] ? errors.loanType[0] : null}
            onChange={handleChange}
            value={formValues?.loanType}
          >
            {loanTypes.map((option) => {
              return (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>
        <Grid item size={{ md: 6, sm: 12, xs: 12 }}>
          <TextField
            required
            fullWidth
            type="text"
            variant="outlined"
            name="loanAmount"
            id="loan-amount"
            label="Loan Amount"
            onChange={handleChange}
            error={errors["loanAmount"] ? true : false}
            helperText={errors["loanAmount"] ? errors.loanAmount[0] : null}
            value={formatNumber(formValues?.loanAmount)}
          />
        </Grid>
        <Grid item size={{ md: 6, sm: 12, xs: 12 }}>
          <TextField
            required
            fullWidth
            type="date"
            slotProps={{
              inputLabel: { shrink: true },
            }}
            variant="outlined"
            name="loanDate"
            id="loan-date"
            label="Loan Date"
            onChange={handleChange}
            error={errors["loanDate"] ? true : false}
            helperText={errors["loanDate"] ? errors.loanDate[0] : null}
            value={formValues?.loanDate || ""}
          />
        </Grid>
        <Grid item size={{ md: 6, sm: 12, xs: 12 }}>
          {render && (
            <TextField
              required
              fullWidth
              disabled={isEmpty(formValues?.loanDate)}
              type="date"
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: {
                  min: moment(formValues?.loanDate).format("YYYY-MM-DD"),
                },
              }}
              variant="outlined"
              name="repaymentStartDate"
              id="repayment-start-date"
              label="Repayment Start Date"
              onChange={handleChange}
              error={errors["repaymentStartDate"] ? true : false}
              helperText={
                errors["repaymentStartDate"]
                  ? errors.repaymentStartDate[0]
                  : null
              }
              value={formValues?.repaymentStartDate}
            />
          )}
        </Grid>
        <Grid item size={{ md: 6, sm: 12, xs: 12 }}>
          <TextField
            required
            fullWidth
            select
            variant="outlined"
            name="repaymentFrequency"
            id="repayment-frequency"
            label="Repayment Frequency"
            onChange={handleChange}
            error={errors["repaymentFrequency"] ? true : false}
            helperText={
              errors["repaymentFrequency"] ? errors.repaymentFrequency[0] : null
            }
            value={formValues?.repaymentFrequency}
          >
            {repaymentFrequencies.map((option) => {
              return (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>
        <Grid item size={{ md: 6, sm: 12, xs: 12 }}>
          <TextField
            fullWidth
            type="text"
            variant="outlined"
            name="otherCharges"
            id="other-charges"
            label="Other charges (If any)"
            placeholder="Fees, forms etc"
            onChange={handleChange}
            value={formatNumber(formValues?.otherCharges)}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 12 }}>
          <h5>Interest</h5>
        </Grid>
        <Grid item size={6}>
          <TextField
            required
            fullWidth
            select
            variant="outlined"
            name="interestOption"
            id="interest-option"
            onChange={handleChange}
            value={formValues?.interestOption}
          >
            {interestOptions.map((option) => {
              return (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>
        <Grid item size={6}>
          <TextField
            required
            fullWidth
            type="text"
            variant="outlined"
            name="interest"
            id="interest"
            label="Percentage"
            slotProps={{
              input: {
                endAdornment: <InputAdornment>%</InputAdornment>,
              },
            }}
            onChange={handleChange}
            error={errors["interest"] ? true : false}
            helperText={errors["interest"] ? errors.interest[0] : null}
            value={formatNumber(formValues?.interest)}
          />
        </Grid>

        <Grid item size={{ xs: 12, md: 12 }}>
          <h5>Duration</h5>
        </Grid>
        <Grid item size={6}>
          <TextField
            required
            fullWidth
            select
            variant="outlined"
            name="durationOption"
            id="duration-option"
            onChange={handleChange}
            value={formValues?.durationOption}
          >
            {durations.map((option) => {
              return (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>
        <Grid item size={6}>
          <TextField
            required
            fullWidth
            type="text"
            variant="outlined"
            name="duration"
            id="duration"
            // label={`Number of ${formValues?.durationOption}`}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment>{formValues?.durationOption}</InputAdornment>
                ),
              },
            }}
            onChange={handleChange}
            error={errors["duration"] ? true : false}
            helperText={errors["duration"] ? errors.duration[0] : null}
            value={formatNumber(formValues?.duration)}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 6, md: 6 }}>
          <div style={{ marginTop: "20px" }}>
            <Button
              disabled={state === "loading"}
              variant="contained"
              fullWidth
              onClick={handleSubmit}
            >
              {state === "loading" && (
                <CircularProgress
                  color="primary"
                  style={{ marginRight: "10px" }}
                  size={20}
                />
              )}
              CALCULATE
            </Button>
          </div>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <div className="header">
        <div className="hd-left">
          <img src={logo} alt="BOT" />
        </div>
        <div className="hd-center">
          <h4>BANK OF TANZANIA LOAN CALCULATOR</h4>
        </div>
        <div className="hd-right"></div>
      </div>
      <div className="container">
        <div className="left">{BuildForm()}</div>
        <div className="right">
          {state === "loading" && (
            <div className="loading-container">
              <CircularProgress size={40} color="primary" />
              <div>Calculating...</div>
            </div>
          )}

          {state === "error" && (
            <div
              style={{ height: "200px" }}
              className="flex-row align-items-center justify-content-center"
            >
              {data}
            </div>
          )}

          {state === "idle" && !isEmpty(data) && (
            <>
              <h5>Loan Summary</h5>
              <div style={{ marginTop: "10px" }}>
                <table className="table-summary">
                  <tr>
                    <td className="light-text">Loan Amount :</td>
                    <td className="text-align-right">
                      {formatNumber(data?.loanAmount, 2)}
                    </td>
                  </tr>
                  <tr>
                    <td className="light-text">Fixed Repayment Amount :</td>
                    <td className="text-align-right">
                      {formatNumber(data?.fixedRepaymentAmount, 2)}
                    </td>
                  </tr>
                  <tr>
                    <td className="light-text">Total Interest :</td>
                    <td className="text-align-right">
                      {calculateTotalInterest()}
                    </td>
                  </tr>
                  <tr>
                    <td className="light-text">Other Charges :</td>
                    <td className="text-align-right">
                      {formatNumber(data?.otherCharges, 2)}
                    </td>
                  </tr>
                  <tr>
                    <td className="light-text">Total Payments :</td>
                    <td className="text-align-right">
                      {calculateTotalPayment()}
                    </td>
                  </tr>
                  <tr>
                    <td className="light-text">Payoff Date :</td>
                    <td className="text-align-right">
                      {data?.schedule[data?.schedule?.length - 1]?.date}
                    </td>
                  </tr>
                </table>
              </div>

              <h5>Amortization Schedule</h5>
              <table className="schedule-table" style={{ marginTop: "10px" }}>
                <thead>
                  <th className=" text-align-center w-60">INSTALLMENT</th>
                  <th className="text-align-center">PAYMENT DATE</th>
                  <th className="text-align-right">PRINCIPAL</th>
                  <th className="text-align-right">INTEREST</th>
                  <th className="text-align-right">PAYMENTS</th>
                  <th className="text-align-right">BALANCE</th>
                </thead>
                <tbody>
                  {data?.schedule?.map((row, index) => (
                    <tr key={`row-${index}`}>
                      <td className="w-60 text-align-center">{index}</td>
                      <td className="text-align-center">{row?.date}</td>
                      <td className="text-align-right">
                        {formatNumber(row?.principal, 2)}
                      </td>
                      <td className="text-align-right">
                        {formatNumber(row?.interest, 2)}
                      </td>
                      <td className="text-align-right">
                        {formatNumber(row?.payments, 2)}
                      </td>
                      <td className="text-align-right">
                        {formatNumber(row?.balance, 2)}
                      </td>
                    </tr>
                  ))}
                  {/* totals */}
                  <tr>
                    <td className="w-60 text-align-center"></td>
                    <td className="text-align-center">
                      <b>{"TOTALS"}</b>
                    </td>
                    <td className="text-align-right">
                      <b> {calculateTotalPrincipal()}</b>
                    </td>
                    <td className="text-align-right">
                      <b> {calculateTotalInterest()}</b>
                    </td>
                    <td className="text-align-right">
                      <b> {calculateTotalPayment(false)}</b>
                    </td>
                    <td className="text-align-right"></td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          <div className="disclaimer">
            <b>DISCLAIMER</b>
            <p style={{ marginTop: "5px" }}>
              The loan calculator is provided by the Bank Of Tanzania for
              informational purposes only and does not constitute financial
              advice, a Loan offer, or approval. Please consult your financial
              institution or advisor for personalized guidance.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default App;
